import { useEffect, useRef, useState } from "react";
import industriesData from "@/data/industries.json";
import countriesData from "@/data/countries.json";
import timezonesData from "@/data/timezones.json";
import countryTz from "@/data/country_timezones.json";

export const INDUSTRIES = industriesData;
export const COUNTRIES = countriesData;

// cities are lazy-loaded per country so the initial bundle stays small
const cityLoaders = {
  gb: () => import("@/data/cities/gb.json"), us: () => import("@/data/cities/us.json"),
  ie: () => import("@/data/cities/ie.json"), au: () => import("@/data/cities/au.json"),
  ca: () => import("@/data/cities/ca.json"), in: () => import("@/data/cities/in.json"),
  ae: () => import("@/data/cities/ae.json"), nz: () => import("@/data/cities/nz.json"),
  de: () => import("@/data/cities/de.json"), fr: () => import("@/data/cities/fr.json"),
  es: () => import("@/data/cities/es.json"), it: () => import("@/data/cities/it.json"),
  nl: () => import("@/data/cities/nl.json"), za: () => import("@/data/cities/za.json"),
};
const cityCache = {};

export async function loadCities(countryCode) {
  const cc = (countryCode || "").toLowerCase();
  if (!cc || !cityLoaders[cc]) return [];
  if (!cityCache[cc]) cityCache[cc] = (await cityLoaders[cc]()).default;
  return cityCache[cc];
}

export function suggestTimezone(countryName) {
  const c = COUNTRIES.find((x) => x.name === countryName);
  return c ? countryTz[c.code] || null : null;
}

export function canonicalTimezone(value) {
  const v = (value || "").trim();
  if (!v) return "";
  if (timezonesData.includes(v)) return v;
  const lower = v.toLowerCase();
  return timezonesData.find((t) => t.toLowerCase() === lower) || v;
}

/** Searchable autocomplete: case-insensitive suggestions, keyboard nav (arrows/Enter/Escape),
 * mouse/touch selection, free-text custom values allowed, ARIA combobox semantics. */
export default function Autocomplete({ value, onChange, options, onOptions, placeholder, testid, maxSuggestions = 8 }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const listId = `${testid}-listbox`;

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const refresh = async (text) => {
    const q = text.trim().toLowerCase();
    let opts = options;
    if (!opts && onOptions) opts = await onOptions();
    if (!opts) { setItems([]); return; }
    const starts = opts.filter((o) => o.toLowerCase().startsWith(q));
    const contains = opts.filter((o) => !o.toLowerCase().startsWith(q) && o.toLowerCase().includes(q));
    setItems([...starts, ...contains].slice(0, maxSuggestions));
    setActive(-1);
  };

  const choose = (val) => {
    onChange(val);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef} role="combobox" aria-expanded={open} aria-haspopup="listbox" aria-owns={listId}>
      <input
        className="w-full rounded-xl border border-neutral-300 px-3.5 py-2 text-sm focus:border-neutral-900 focus:outline-none"
        value={open ? query : value}
        placeholder={placeholder}
        data-testid={testid}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-activedescendant={active >= 0 ? `${testid}-option-${active}` : undefined}
        onFocus={(e) => { setQuery(""); setOpen(true); refresh(""); e.target.select?.(); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); refresh(e.target.value); if (!open) onChange(e.target.value); }}
        onBlur={() => { if (open && query.trim()) onChange(query.trim()); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, items.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === "Enter") {
            e.preventDefault();
            if (active >= 0 && items[active]) choose(items[active]);
            else if (query.trim()) choose(query.trim());  // custom value allowed
            else setOpen(false);
          } else if (e.key === "Escape") { setOpen(false); setQuery(""); }
        }}
      />
      {open && items.length > 0 && (
        <ul id={listId} role="listbox" data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-1 max-h-52 w-full overflow-y-auto rounded-xl border border-neutral-200 bg-white py-1 shadow-lg">
          {items.map((o, i) => (
            <li key={o} id={`${testid}-option-${i}`} role="option" aria-selected={active === i}
              data-testid={`${testid}-option-${i}`}
              onMouseDown={(e) => { e.preventDefault(); choose(o); }}
              onMouseEnter={() => setActive(i)}
              className={`cursor-pointer px-3.5 py-2 text-sm ${active === i ? "bg-neutral-100 font-semibold" : "text-neutral-700"}`}>
              {o}
            </li>
          ))}
        </ul>
      )}
      {open && query.trim() && items.length === 0 && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-2 text-xs text-neutral-500 shadow-lg" data-testid={`${testid}-custom`}>
          Press Enter to use “{query.trim()}”
        </div>
      )}
    </div>
  );
}
